import React, { ChangeEvent } from 'react'
import clsx from 'clsx'
import { Toggle as ToggleStyles } from '@airbus/elements/elements'
import uniqueId from 'lodash.uniqueid'
import { createUseStyles } from '../../lib/styling'
import useDeprecatedProps from '../../lib/hooks/useDeprecatedProps'
import useControlled from '../../lib/hooks/useControlled'
import { mockInputChangeEvent } from '../../lib/utils'
import { CommonProps } from '../helperTypes'

type Props = {
  /** If `true`, the component is checked */
  checked?: boolean
  /** The default checked status for an uncontrolled component */
  defaultChecked?: boolean
  /**
   * The id that will be passed to the underlying input element.
   */
  id?: string
  /** The label to show next to the `<input />` */
  label?: string
  /** The callback for when the input's state changes */
  onChange?: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void
  /**
   * The size of the toggle
   * @default medium
   */
  size?: 'small' | 'medium'
}

type DeprecatedProps = {
  dark?: boolean
  name?: string
}

export type ToggleProps = Props & DeprecatedProps & CommonProps

const handleDeprecatedProps = (props: ToggleProps) => {
  const { name, ...rest } = props
  return { id: name, ...rest }
}

const useStyles = createUseStyles(ToggleStyles, 'toggle')

const Toggle = (props: ToggleProps) => {
  const {
    id: propsId,
    checked: checkedProp,
    defaultChecked,
    onChange,
    label,
    size,
    className,
    style,
    ...rest
  } = handleDeprecatedProps(props)
  useDeprecatedProps('Toggle', { name: 'id', dark: undefined }, props)
  const defaultId = React.useRef<string>(uniqueId('Toggle-'))
  const inputEl = React.useRef(null)
  const classes = useStyles()
  const [checked, setCheckedState] = useControlled({
    value: checkedProp,
    defaultValue: defaultChecked,
  })

  const id = propsId || defaultId.current
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setCheckedState(e.target.checked)
    if (onChange) {
      onChange(e, e.target.checked)
    }
  }
  return (
    <div className={clsx(className, classes.root, size && classes[`_${size}`])} style={style}>
      <input
        className={classes.input}
        ref={inputEl}
        id={id}
        type='checkbox'
        checked={checked}
        onChange={handleChange}
        onKeyDown={(evt) => {
          if (evt.key !== 'Enter') return
          handleChange(mockInputChangeEvent(!checked, true))
        }}
        {...rest}
      />
      <label className={clsx(classes.label, { [classes.label_none]: !label })} htmlFor={id}>
        {label || <>&nbsp;</>}
      </label>
    </div>
  )
}

export default Toggle
